import { COMMISSION, MEMBERSHIP, SUPPORT } from '@services/MarppleShop/features/Membership/constant';
import {
  makeCalculateIcon,
  makeCircleChartIcon,
  makeDashboardIcon,
  makeDigitalIcon,
  makeFAQIcon,
  makeGuideIcon,
  makeHomeIcon,
  makeInquiryIcon,
  makeMembershipIcon,
  makeOutLinkIcon,
  makePBIcon,
  makePODIcon,
  makeReportIcon,
  makeShopIcon,
  makeStatisticsIcon,
  makeYoutubeIcon,
} from '@services/MarppleShop/marppleShopStudioApp/components/atoms/Icon';
import { html } from '../../../../shared/utils/html.ts';
import { isMembershipSupported } from '../../../Util/S/Function/membership';
import { UtilS } from '../../../Util/S/Function/module/UtilS.js';
import { ACTION, ACTION_TARGET, SERVICE } from '../../Authority/S/constant.js';
import { checkActions } from '../../Authority/S/util.js';
import { CREW_DOMAIN_NAME } from '../../Crew/S/constant';
import settings_klass from '../F/setting_menu.module.scss';
import { STUDIO_CREATOR_GUIDE_URL, STUDIO_UPDATE_NOTE_URL } from './constant.js';
import { messages } from './messages_id.js';
import { MShopUtilConstantS } from '@modules/MShop/Util/S/Constant/module/MShopUtilConstantS';
import { Locals } from '@services/MarppleShop/shared/type/locals.ts';

/**
 * `menu_id`를 lnb link converting 해주는 함수
 * - do not export, 감싸진 함수 사용할 것!
 * @see {generateMarppleShopStudioUrl}
 */
const convertMenuIdToLink = (menu_id: keyof typeof LNB_MENU_ID): string => {
  return menu_id.toLowerCase();
};

export const LNB_INVITE_MENU = {
  title: messages.menus.invite_info,
  link: 'invite_info',
  page_name: 'invite_info',
};

/**
 * 새로 만들어지는 페이지들은 여기에 추가하여 사용
 */
export const LNB_MENU_ID = {
  WELCOME: 'WELCOME',
  DASHBOARD: 'DASHBOARD',
  ORDER_OVERVIEW: 'ORDER_OVERVIEW',
  PRODUCT_ANALYSIS: 'PRODUCT_ANALYSIS',
  INTEGRATED_ORDER_SEARCH: 'INTEGRATED_ORDER_SEARCH',

  PRODUCT_MANAGEMENT: 'PRODUCT_MANAGEMENT',
  SALES_MANAGEMENT: 'SALES_MANAGEMENT',

  PRODUCTS: 'PRODUCTS',
  'CUSTOM-ASSET-PRODUCTS': 'CUSTOM-ASSET-PRODUCTS',
  'CUSTOM-USER-PRODUCTS': 'CUSTOM-USER-PRODUCTS',
  PB_PRODUCTS: 'PB_PRODUCTS',
  PB_INVENTORY_MANAGEMENT: 'PB_INVENTORY_MANAGEMENT',
  DIGITAL_PRODUCTS: 'DIGITAL_PRODUCTS',
  CREATOR_DESIGN_PRODUCTS: 'CREATOR_DESIGN_PRODUCTS',
  POPUP: 'POPUP',
  MEMBERSHIP_COMMISSION_SUPPORT: 'MEMBERSHIP_COMMISSION_SUPPORT',
  MEMBERSHIP,
  MEMBERSHIP_GUIDE: 'MEMBERSHIP_GUIDE',
  MEMBERSHIP_PROFIT_SHARE: 'MEMBERSHIP_PROFIT_SHARE',
  COMMISSION,
  SUPPORT,
  MEMBERSHIP_COMMISSION_SUPPORT_MANAGEMENT: 'MEMBERSHIP_COMMISSION_SUPPORT_MANAGEMENT',
  STORE_DELIVERY: 'STORE_DELIVERY',

  ADJUSTMENTS: 'ADJUSTMENTS',
  WITHDRAWS_V2: 'WITHDRAWS_V2',

  CREATOR_SUPPORT: 'CREATOR_SUPPORT',
  QUESTION_LIST: 'QUESTION_LIST',
  SOLUTIONS: 'SOLUTIONS',
  TERMS: 'TERMS',
  CUSTOMER_INQUIRY: 'CUSTOMER_INQUIRY',
} as const;

export const LNB_IS_NOT_AGREE_MENUS = [
  {
    title: messages.footer.terms,
    id: LNB_MENU_ID.TERMS,
    page_name: LNB_MENU_ID.TERMS,
    link: convertMenuIdToLink(LNB_MENU_ID.TERMS),
    dataShow() {
      return T.lang == 'kr';
    },
  },
  // {
  //   title: messages.menus.account,
  //   link: 'account',
  //   page_name: 'account',
  //   dataShow(user: Locals['login_user']) {
  //     return user.is_seller;
  //   },
  // },
  // @see https://www.notion.so/marpple/1a05328163528086b901c7d706dd9307?pvs=4
  // {
  //   title: messages.menus.faq,
  //   link: 'help',
  //   page_name: 'help',
  // },
];

export const generateMarppleShopStudioUrl = (option: {
  /**
   * sub_path 없이 이동하려면 `menu_id`에 `null` 할당하여 사용
   */
  menu_id: keyof typeof LNB_MENU_ID | null;
  domain_name: string;
}) => {
  const { menu_id, domain_name } = option;

  if (!menu_id) {
    return `/${T.lang}/${domain_name}/settings`;
  }
  const sub_path = convertMenuIdToLink(menu_id);
  return `/${T.lang}/${domain_name}/settings/${sub_path}`;
};

/**
 * Generate Ness admin URL with optional menu_id-based sub_path.
 * @param option - Configuration object
 * @param option.menu_id - Menu ID for sub-path; pass null to omit sub_path
 * @param option.domain_name - Domain name for the URL
 * @returns Complete Ness admin URL path
 */
export const generateNessAdminUrl = (option: {
  /**
   * sub_path 없이 이동하려면 `menu_id`에 `null` 할당하여 사용
   */
  menu_id: keyof typeof LNB_MENU_ID | null;
  domain_name: string;
}) => {
  const { menu_id, domain_name } = option;

  if (!menu_id) {
    return `/${T.lang}/${domain_name}/admin`;
  }
  const sub_path = convertMenuIdToLink(menu_id);
  return `/${T.lang}/${domain_name}/admin/${sub_path}`;
};

export const LNB_TOP_CUSTOM_SWITCH_MENU = {
  // 커스텀 굿즈 제작 메뉴
  id: 'make_products',
  title: messages.menus.make_products,
  icon: makePODIcon({ html }),
  collapses: [
    {
      title: messages.menus.static_products,
      link: convertMenuIdToLink(LNB_MENU_ID.PRODUCTS),
      page_name: 'products',
      dataShow(user: Locals['login_user']) {
        return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
      },
    },
    {
      title: messages.menus.custom_asset_products,
      link: convertMenuIdToLink(LNB_MENU_ID['CUSTOM-ASSET-PRODUCTS']),
      page_name: 'custom-asset-products',
      dataShow(user: Locals['login_user']) {
        return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
      },
    },
    {
      title: messages.menus.custom_user_products,
      link: convertMenuIdToLink(LNB_MENU_ID['CUSTOM-USER-PRODUCTS']),
      page_name: 'custom-user-products',
      dataShow(user: Locals['login_user']) {
        return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
      },
    },
    {
      title: messages.menus.orders,
      link: 'orders',
      page_name: 'orders',
      dataShow(user: Locals['login_user']) {
        return checkActions({ actions: ACTION.READ_ORDERS, target: ACTION_TARGET.STORE }, { user });
      },
    },
  ],
};

/* 여기는 ness 스튜디오 메뉴 */
export const LNB_TOP_MENUS = [
  {
    // 대시보드
    id: LNB_MENU_ID.DASHBOARD,
    page_name: LNB_MENU_ID.DASHBOARD,
    link: convertMenuIdToLink(LNB_MENU_ID.DASHBOARD),
    title: messages.menus.dashboard,
    // title_suffix: new StudioBetaTag().toHtmlSSR(),
    icon: makeDashboardIcon({ html }),
    dataShow() {
      return T.lang === 'kr' && UtilS.isMarppleShopApp();
    },
  },
  {
    // 판매 현황
    id: LNB_MENU_ID.ORDER_OVERVIEW,
    page_name: LNB_MENU_ID.ORDER_OVERVIEW,
    link: convertMenuIdToLink(LNB_MENU_ID.ORDER_OVERVIEW),
    title: messages.menus.order_overview,
    icon: makeCircleChartIcon({ html }),
    dataShow(user: Locals['login_user']) {
      return (
        T.lang === 'kr' &&
        UtilS.isMarppleShopApp() &&
        checkActions({ actions: ACTION.READ_ORDERS, target: ACTION_TARGET.STORE }, { user })
      );
    },
  },
  {
    // 굿즈 제작
    id: 'make_products',
    title: messages.menus.make_products,
    icon: makePODIcon({ html }),
    collapses: [
      {
        title: messages.menus.products,
        link: convertMenuIdToLink(LNB_MENU_ID.PRODUCTS),
        page_name: 'products',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
      {
        title: messages.menus.orders,
        link: 'orders',
        page_name: 'orders',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.READ_ORDERS, target: ACTION_TARGET.STORE }, { user });
        },
      },
    ],
  },
  {
    // 상품 판매
    id: 'seller_products',
    title: messages.menus.seller_product,
    icon: makePBIcon({ html }),
    dataShow() {
      return T.lang == 'kr';
    },
    collapses: [
      {
        title: messages.menus.real_pb_products,
        link: convertMenuIdToLink(LNB_MENU_ID.PB_PRODUCTS),
        page_name: 'pb-products',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
      {
        title: messages.menus.inventory_management,
        link: 'inventory_management',
        page_name: 'pb-inventory-management',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
      {
        title: messages.menus.pb_orders,
        link: 'pb_orders',
        page_name: 'pb-orders',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
      {
        title: messages.menus.pb_item_orders,
        link: 'pb_item_orders',
        page_name: 'pb-item-orders',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
      {
        title: messages.menus.pb_refund_orders,
        link: 'pb_refund_orders',
        page_name: 'pb-refund-orders',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
    ],
  },
  {
    // 디지털/전자책 판매
    id: 'digital_product',
    title: messages.menus.digital_product,
    icon: makeDigitalIcon({ html }),
    dataShow() {
      return T.lang == 'kr';
    },
    collapses: [
      {
        title: messages.menus.make_digital_products,
        link: convertMenuIdToLink(LNB_MENU_ID.DIGITAL_PRODUCTS),
        page_name: 'digital-products',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
      {
        title: messages.menus.digital_orders,
        link: 'digital_item_orders',
        page_name: 'digital-item-orders',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
    ],
  },
  {
    // 고객 문의 관리
    id: LNB_MENU_ID.QUESTION_LIST,
    page_name: LNB_MENU_ID.QUESTION_LIST,
    title: messages.menus.question_list,
    link: 'question_list',
    icon: makeInquiryIcon({ html }),
    dataShow() {
      if (UtilS.isNessApp()) {
        return false;
      }
      return T.lang == 'kr';
    },
  },
  {
    // 샵 관리
    id: 'shop_management',
    title: messages.menus.shop_management,
    icon: makeShopIcon({ html }),
    dataShow(user: Locals['login_user']) {
      return checkActions(
        {
          actions: ACTION.UPDATE_STORE_NAME,
          target: ACTION_TARGET.STORE,
        },
        { user },
      );
    },
    collapses: [
      {
        title: messages.menus.styles,
        link: 'styles',
        page_name: 'styles',
      },
      {
        title: messages.menus.arrangement,
        link: 'arrangement',
        page_name: 'arrangement',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
      {
        title: messages.menus.shop_info,
        link: 'shop_info',
        page_name: 'shop_info',
      },
      {
        title: messages.menus.ecommerce_info,
        link: 'ecommerce_info',
        page_name: 'ecommerce-info',
        dataShow(user: Locals['login_user']) {
          return (
            T.lang == 'kr' &&
            checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user })
          );
        },
      },
      {
        id: LNB_MENU_ID.POPUP,
        page_name: LNB_MENU_ID.POPUP,
        title: messages.menus.popup,
        link: 'popup',
        dataShow() {
          if (UtilS.isNessApp()) {
            return false;
          }
          return T.lang == 'kr';
        },
      },
      {
        title: messages.menus.open_api,
        link: 'open_api',
        page_name: 'open_api',
        dataShow() {
          return T.lang == 'kr';
        },
      },
      {
        title: messages.menus.external_services,
        link: 'external_services',
        page_name: 'external_services',
        dataShow() {
          return false;
          // return T.lang == 'kr';
        },
      },
    ],
  },
  {
    // 정산 관리
    id: 'adjustments',
    title: messages.menus.adjustments,
    icon: makeCalculateIcon({ html }),
    dataShow(user: Locals['login_user']) {
      return checkActions(
        {
          actions: ACTION.READ_ADJUSTMENTS,
          target: ACTION_TARGET.STORE,
          service: SERVICE.WITHDRAW,
          pass_no_service: false,
        },
        { user },
      );
    },
    collapses: [
      {
        title: messages.menus.adjustments,
        link: 'adjustments',
        page_name: 'adjustments',
        dataShow(user: Locals['login_user']) {
          return checkActions(
            {
              actions: ACTION.READ_ADJUSTMENTS,
              target: ACTION_TARGET.STORE,
              service: SERVICE.WITHDRAW,
              pass_no_service: false,
            },
            { user },
          );
        },
      },
      // {
      //   title: messages.menus.withdraws,
      //   link: 'withdraws',
      //   page_name: 'withdraws',
      //   dataShow(user: Locals['login_user']) {
      //     return checkActions(
      //       {
      //         actions: ACTION.READ_WITHDRAWS,
      //         target: ACTION_TARGET.STORE,
      //         service: SERVICE.WITHDRAW,
      //         pass_no_service: false,
      //       },
      //       { user },
      //     );
      //   },
      // },
      {
        title: messages.menus.bank_account,
        link: 'bank_account',
        page_name: 'bank_account',
        dataShow(user: Locals['login_user']) {
          return checkActions(
            {
              actions: ACTION.READ_BANK_ACCOUNT,
              target: ACTION_TARGET.STORE,
              service: SERVICE.WITHDRAW,
              pass_no_service: false,
              user_exceptions: [1184090, 1372089],
            },
            { user },
          );
        },
      },
    ],
  },
  {
    // 통계
    id: 'analytics',
    title: messages.menus.analytics,
    link: 'analytics',
    page_name: 'analytics',
    icon: makeStatisticsIcon({ html }),
    dataShow(user: Locals['login_user']) {
      return checkActions({ actions: ACTION.READ_SALES_ANALYTICS, target: ACTION_TARGET.STORE }, { user });
    },
  },
  {
    // 내 샵 바로가기
    id: 'my_shop',
    title: messages.menus.my_shop,
    blank(domain_name: string) {
      return `/${T.lang}/${domain_name}`;
    },
    dataShow(_: unknown, is_app: boolean) {
      return !is_app;
    },
    icon: makeShopIcon({ html }),
  },
] as const;
export const LNB_MID_MENUS = [
  {
    // 유튜브에서 판매
    id: 'youtube',
    title: messages.menus.youtube,
    link: 'youtube',
    page_name: 'youtube',
    icon: makeYoutubeIcon({ html }),
  },
  {
    // 크리에이터 가이드
    id: 'seller_guide',
    title: messages.menus.seller_guide,
    blank() {
      return STUDIO_CREATOR_GUIDE_URL();
    },
    icon: makeGuideIcon({ html }),
    dataShow() {
      return T.lang != 'jp';
    },
  },
  {
    // FAQ
    id: 'faq',
    title: messages.menus.faq,
    link: 'help',
    page_name: 'help',
    icon: makeFAQIcon({ html }),
  },
  {
    // 신고 접수
    id: LNB_MENU_ID.SOLUTIONS,
    title: messages.menus.solution,
    link: convertMenuIdToLink(LNB_MENU_ID.SOLUTIONS),
    page_name: LNB_MENU_ID.SOLUTIONS,
    icon: makeReportIcon({ html }),
  },
];
export const LNB_BOTTOM_MENUS = [
  {
    // 초대
    id: 'invite',
    title: messages.menus.invite,
    link: 'invite',
    page_name: 'invite',
    dataShow() {
      return false;
    },
  },
  {
    // 업데이트 노트
    id: 'update_note',
    title: messages.menus.update_note,
    klass: 'lnb-update-note',
    dataShow() {
      return T.lang == 'kr';
    },
    blank() {
      return STUDIO_UPDATE_NOTE_URL;
    },
  },
  {
    // CCC
    id: 'creator_community_club',
    title: messages.menus.creator_community_club,
    dataShow() {
      return T.lang == 'kr';
    },
    blank() {
      return 'https://creatorscommunity.club/';
    },
  },
];

const isSoopUser = (user: Locals['login_user']) => {
  return user?._?.crew?.id === MShopUtilConstantS.SOOP_MARPPLESHOP_CREW_ID;
};

/* 여기는 마플샵 스튜디오 메뉴 */
export const MPS_LNB_MENUS = [
  {
    // 시작하기
    id: LNB_MENU_ID.WELCOME,
    page_name: LNB_MENU_ID.WELCOME,
    link: convertMenuIdToLink(LNB_MENU_ID.WELCOME),
    title: ET('mps2::seller_studio::navigation::welcome'),
    icon: makeHomeIcon({ html }),
    dataShow() {
      return T.lang === 'kr' && UtilS.isMarppleShopApp();
    },
  },
  {
    // 상품 관리
    id: LNB_MENU_ID.PRODUCT_MANAGEMENT,
    title: ET('mps2::seller_studio::navigation::product_management'),
    icon: makePODIcon({ html }),
    collapses: [
      {
        title: ET('mps2::seller_studio::navigation::mps_product'),
        link: convertMenuIdToLink(LNB_MENU_ID.PRODUCTS),
        page_name: 'products',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
      {
        title: ET('mps2::seller_studio::navigation::creator_product'),
        link: convertMenuIdToLink(LNB_MENU_ID.PB_PRODUCTS),
        page_name: 'pb-products',
        dataShow(user: Locals['login_user']) {
          return (
            !isSoopUser(user) &&
            checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user })
          );
        },
      },
      {
        title: ET('mps2::seller_studio::navigation::digital_product'),
        link: convertMenuIdToLink(LNB_MENU_ID.DIGITAL_PRODUCTS),
        page_name: 'digital-products',
        dataShow(user: Locals['login_user']) {
          return (
            !isSoopUser(user) &&
            checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user })
          );
        },
      },
      {
        id: LNB_MENU_ID.CREATOR_DESIGN_PRODUCTS,
        title: ET('mps2::seller_studio::navigation::creator_design'),
        link: convertMenuIdToLink(LNB_MENU_ID.CREATOR_DESIGN_PRODUCTS),
        page_name: LNB_MENU_ID.CREATOR_DESIGN_PRODUCTS,
        dataShow(user: Locals['login_user']) {
          return (
            !isSoopUser(user) &&
            T.lang === 'kr' &&
            checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user })
          );
        },
      },
    ],
  },
  {
    // 판매 관리
    id: LNB_MENU_ID.SALES_MANAGEMENT,
    title: ET('mps2::seller_studio::navigation::sales_management'),
    icon: makePBIcon({ html }),
    dataShow() {
      return T.lang == 'kr';
    },
    collapses: [
      {
        id: LNB_MENU_ID.INTEGRATED_ORDER_SEARCH,
        page_name: LNB_MENU_ID.INTEGRATED_ORDER_SEARCH,
        link: convertMenuIdToLink(LNB_MENU_ID.INTEGRATED_ORDER_SEARCH),

        title: ET('mps2::seller_studio::navigation::integrated_order_search'),
        dataShow(user: Locals['login_user']) {
          return (
            T.lang === 'kr' &&
            UtilS.isMarppleShopApp() &&
            checkActions({ actions: ACTION.READ_ORDERS, target: ACTION_TARGET.STORE }, { user })
          );
        },
      },
      {
        title: messages.menus.inventory_management,
        link: 'inventory_management',
        // page_name: 'pb-inventory-management',
        page_name: LNB_MENU_ID.PB_INVENTORY_MANAGEMENT,
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
      {
        title: ET('mps2::seller_studio::navigation::shipping'),
        link: 'pb_item_orders',
        page_name: 'pb-item-orders',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
      {
        title: messages.menus.pb_refund_orders,
        link: 'pb_refund_orders',
        page_name: 'pb-refund-orders',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
    ],
  },
  {
    // 멤버십 / 커미션 / 후원
    id: LNB_MENU_ID.MEMBERSHIP_COMMISSION_SUPPORT,
    title: ET('mps2::seller_studio::navigation::membership_commission_support'),
    icon: makeMembershipIcon({ html }),
    dataShow() {
      return isMembershipSupported();
    },
    collapses: [
      {
        title: ET('mps2::seller_studio::navigation::membership_guide'),
        link: convertMenuIdToLink(LNB_MENU_ID.MEMBERSHIP_GUIDE),
        page_name: LNB_MENU_ID.MEMBERSHIP_GUIDE,
      },
      {
        title: ET('mps2::seller_studio::navigation::membership'),
        link: convertMenuIdToLink(LNB_MENU_ID.MEMBERSHIP),
        page_name: LNB_MENU_ID.MEMBERSHIP,
        dataShow(user: Locals['login_user']) {
          return checkActions(
            {
              actions: ACTION.MANAGE_MEMBERSHIP,
              target: ACTION_TARGET.STORE,
            },
            { user },
          );
        },
      },
      {
        title: ET('mps2::seller_studio::navigation::commission'),
        link: convertMenuIdToLink(LNB_MENU_ID.COMMISSION),
        page_name: LNB_MENU_ID.COMMISSION,
        dataShow(user: Locals['login_user']) {
          return checkActions(
            {
              actions: ACTION.MANAGE_MEMBERSHIP,
              target: ACTION_TARGET.STORE,
            },
            { user },
          );
        },
      },
      {
        title: ET('mps2::seller_studio::navigation::support'),
        link: convertMenuIdToLink(LNB_MENU_ID.SUPPORT),
        page_name: LNB_MENU_ID.SUPPORT,
        dataShow(user: Locals['login_user']) {
          return checkActions(
            {
              actions: ACTION.MANAGE_MEMBERSHIP,
              target: ACTION_TARGET.STORE,
            },
            { user },
          );
        },
      },
      {
        title: ET('mps2::seller_studio::navigation::membership_commission_support_management'),
        link: convertMenuIdToLink(LNB_MENU_ID.MEMBERSHIP_COMMISSION_SUPPORT_MANAGEMENT),
        page_name: LNB_MENU_ID.MEMBERSHIP_COMMISSION_SUPPORT_MANAGEMENT,
        dataShow(user: Locals['login_user']) {
          return checkActions(
            {
              actions: ACTION.MANAGE_MEMBERSHIP,
              target: ACTION_TARGET.STORE,
            },
            { user },
          );
        },
      },
      {
        title: ET('mps2::seller_studio::navigation::membership_adjustments_share'),
        link: convertMenuIdToLink(LNB_MENU_ID.MEMBERSHIP_PROFIT_SHARE),
        page_name: LNB_MENU_ID.MEMBERSHIP_PROFIT_SHARE,
        dataShow(user: Locals['login_user']) {
          const is_marpple_crew_seller = user?._?.crew?.domain_name == CREW_DOMAIN_NAME.MARPPLE;
          return (
            !is_marpple_crew_seller &&
            checkActions(
              {
                actions: [ACTION.SET_MEMBERSHIP_ADJUSTMENT_SHARE, ACTION.MANAGE_MEMBERSHIP],
                target: ACTION_TARGET.STORE,
              },
              { user },
            )
          );
        },
      },
    ],
  },
  {
    // 고객 관리
    id: LNB_MENU_ID.CUSTOMER_INQUIRY,
    title: ET('mps2::seller_studio::navigation::customer_management'),
    icon: makeInquiryIcon({ html }),
    dataShow() {
      return T.lang == 'kr';
    },
    collapses: [
      {
        // 상품 문의
        id: LNB_MENU_ID.QUESTION_LIST,
        page_name: LNB_MENU_ID.QUESTION_LIST,
        link: convertMenuIdToLink(LNB_MENU_ID.QUESTION_LIST),
        title: ET('mps2::seller_studio::navigation::product_inquiry'),
        dataShow() {
          return T.lang === 'kr' && UtilS.isMarppleShopApp();
        },
      },
      {
        // 신고 접수
        id: LNB_MENU_ID.SOLUTIONS,
        title: messages.menus.solution,
        link: convertMenuIdToLink(LNB_MENU_ID.SOLUTIONS),
        page_name: LNB_MENU_ID.SOLUTIONS,
      },
    ],
  },
  {
    // 샵 관리
    id: 'shop_management',
    title: messages.menus.shop_management,
    icon: makeShopIcon({ html }),
    dataShow(user: Locals['login_user']) {
      return checkActions(
        {
          actions: ACTION.UPDATE_STORE_NAME,
          target: ACTION_TARGET.STORE,
        },
        { user },
      );
    },
    collapses: [
      {
        title: messages.menus.styles,
        link: 'styles',
        page_name: 'styles',
      },
      {
        title: messages.menus.arrangement,
        link: 'arrangement',
        page_name: 'arrangement',
        dataShow(user: Locals['login_user']) {
          return checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user });
        },
      },
      {
        id: LNB_MENU_ID.POPUP,
        page_name: LNB_MENU_ID.POPUP,
        title: messages.menus.popup,
        link: 'popup',
        dataShow() {
          return T.lang == 'kr';
        },
      },
      {
        title: messages.menus.shop_info,
        link: 'shop_info',
        page_name: 'shop_info',
      },
      {
        title: messages.menus.ecommerce_info,
        link: 'ecommerce_info',
        page_name: 'ecommerce-info',
        dataShow(user: Locals['login_user']) {
          return (
            T.lang == 'kr' &&
            checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user })
          );
        },
      },
      {
        id: LNB_MENU_ID.STORE_DELIVERY,
        page_name: 'store_delivery',
        title: messages.menus.store_delivery,
        link: 'store_delivery',
        dataShow(user: Locals['login_user']) {
          return (
            T.lang == 'kr' &&
            checkActions({ actions: ACTION.MAKE_PRODUCT, target: ACTION_TARGET.STORE }, { user })
          );
        },
      },
      {
        id: 'youtube',
        title: ET('mps2::seller_studio::navigation::youtube_connect'),
        link: 'youtube',
        page_name: 'youtube',
      },
      {
        title: ET('mps2::seller_studio::navigation::open_api'),
        link: 'open_api',
        page_name: 'open_api',
        dataShow() {
          return T.lang == 'kr';
        },
      },
      {
        title: messages.menus.external_services,
        link: 'external_services',
        page_name: 'external_services',
        dataShow() {
          return false;
          // return T.lang == 'kr';
        },
      },
    ],
  },
  {
    // 정산 관리
    id: 'adjustments',
    title: ET('mps2::seller_studio::navigation::adjustments'),
    icon: makeCalculateIcon({ html }),
    dataShow(user: Locals['login_user']) {
      return checkActions(
        {
          actions: ACTION.READ_ADJUSTMENTS,
          target: ACTION_TARGET.STORE,
          service: SERVICE.WITHDRAW,
          pass_no_service: false,
        },
        { user },
      );
    },
    collapses: [
      {
        title: ET('mps2::seller_studio::navigation::product_adjustments'),
        link: convertMenuIdToLink(LNB_MENU_ID.WITHDRAWS_V2),
        page_name: LNB_MENU_ID.WITHDRAWS_V2,
        dataShow(user: Locals['login_user']) {
          return checkActions(
            {
              actions: ACTION.READ_ADJUSTMENTS,
              target: ACTION_TARGET.STORE,
              service: SERVICE.WITHDRAW,
              pass_no_service: false,
            },
            { user },
          );
        },
      },
      {
        title: messages.menus.bank_account,
        link: 'bank_account',
        page_name: 'bank_account',
        dataShow(user: Locals['login_user']) {
          return checkActions(
            {
              actions: ACTION.READ_BANK_ACCOUNT,
              target: ACTION_TARGET.STORE,
              service: SERVICE.WITHDRAW,
              pass_no_service: false,
              user_exceptions: [1184090, 1372089],
            },
            { user },
          );
        },
      },
    ],
  },
  {
    // 통계
    id: 'analytics',
    title: messages.menus.analytics,
    icon: makeDashboardIcon({ html }),
    dataShow(user: Locals['login_user']) {
      return (
        T.lang === 'kr' &&
        UtilS.isMarppleShopApp() &&
        checkActions({ actions: ACTION.READ_SALES_ANALYTICS, target: ACTION_TARGET.STORE }, { user })
      );
    },
    collapses: [
      {
        // 대시보드
        id: LNB_MENU_ID.DASHBOARD,
        page_name: LNB_MENU_ID.DASHBOARD,
        link: convertMenuIdToLink(LNB_MENU_ID.DASHBOARD),
        title: messages.menus.dashboard,
        dataShow() {
          return T.lang === 'kr' && UtilS.isMarppleShopApp();
        },
      },
      {
        // 상품별 판매 분석
        id: LNB_MENU_ID.PRODUCT_ANALYSIS,
        page_name: LNB_MENU_ID.PRODUCT_ANALYSIS,
        link: convertMenuIdToLink(LNB_MENU_ID.PRODUCT_ANALYSIS),
        title: ET('mps2::seller_studio::navigation::product_sales_analytics'),
        dataShow(user: Locals['login_user']) {
          return (
            T.lang === 'kr' &&
            UtilS.isMarppleShopApp() &&
            checkActions({ actions: ACTION.READ_ORDERS, target: ACTION_TARGET.STORE }, { user })
          );
        },
      },
    ],
  },
] as const;

// 지원센터
export const MPS_LNB_SUPPORT_MENUS = [
  {
    // 크리에이터 가이드
    id: 'seller_guide',
    title: messages.menus.seller_guide,
    title_suffix: html`<span class="${settings_klass.title_icon_suffix}">${makeOutLinkIcon({ html })}</span>`,
    klass: 'creator-guide',
    blank() {
      return STUDIO_CREATOR_GUIDE_URL();
    },
    dataShow() {
      return T.lang != 'jp';
    },
  },
  {
    // 업데이트 노트
    id: 'update_note',
    title: messages.menus.update_note,
    title_suffix: html`<span class="${settings_klass.title_icon_suffix}">${makeOutLinkIcon({ html })}</span>`,
    klass: 'lnb-update-note',
    dataShow() {
      return T.lang == 'kr';
    },
    blank() {
      return STUDIO_UPDATE_NOTE_URL;
    },
  },
  {
    // FAQ
    id: 'faq',
    title: messages.menus.faq,
    link: 'help',
    page_name: 'help',
  },
];
